// Axios client for the Transaction Wizard (obj_type "U") — the authoring API
// (`/api/wizards/*`, design Appendix A.3; authoring only, never on a bound
// domain) and the render read the Run view mounts the runner from
// (`/api/designers/load-wizard-object`, RUN_APP).
//
// The catalog is the designer's whole picture of the project: every
// transaction's forms and columns (with the mandatory / protect / item_change
// facts the column pickers badge), its action buttons, the V / D / R objects
// and their criteria / prompt NAMES, the other wizards' signatures and the
// Assistant's intents. Its wire shape is normalised here, once, so a backend
// that sends a list where a map was expected (or `params` for `criteria`)
// cannot break every picker separately.

import api from './client'
import type {
  WizardFetchResponse, WizardFrameValues, WizardInput, WizardModel,
  WizardScanResponse,
} from '@twasta/page-runtime/wizard/contract'
import type { TransactionModel } from '@twasta/page-runtime/txn/types'

// ── Model ────────────────────────────────────────────────────────────────

export interface WizardSummary {
  obj_name: string
  title?: string
  base_object?: string
  mode?: string
}

export interface WizardCheckIssue {
  code: string
  message: string
  step?: string | null
  path?: string | null
}

export interface WizardCheckResult {
  ok?: boolean
  errors: WizardCheckIssue[]
  warnings: WizardCheckIssue[]
  /** The writable set (design §12.3): form_no → columns, plus which forms the
   *  wizard may add / delete lines on. */
  writable?: {
    columns?: Record<string, string[]>
    add_lines?: string[]
    delete_lines?: string[]
    [key: string]: unknown
  }
}

function asCheck(raw: unknown): WizardCheckResult {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  // The validator result may come bare or under `check`.
  const c = (r.check && typeof r.check === 'object' ? r.check : r) as Record<string, unknown>
  return {
    ok: c.ok as boolean | undefined,
    errors: Array.isArray(c.errors) ? (c.errors as WizardCheckIssue[]) : [],
    warnings: Array.isArray(c.warnings) ? (c.warnings as WizardCheckIssue[]) : [],
    writable: (c.writable && typeof c.writable === 'object'
      ? c.writable : undefined) as WizardCheckResult['writable'],
  }
}

/** Unwrap `{wizard: {...}}` (the file shape) to the inner model. */
function innerModel(raw: unknown): WizardModel | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  if (r.wizard && typeof r.wizard === 'object') return r.wizard as WizardModel
  return r as unknown as WizardModel
}

export async function listWizards(project: string): Promise<WizardSummary[]> {
  const res = await api.get(`/wizards/${encodeURIComponent(project)}`)
  return (res.data?.wizards || []) as WizardSummary[]
}

/** The saved model (a blank one when the file does not exist yet). */
export async function loadWizard(project: string, objName: string): Promise<WizardModel | null> {
  const res = await api.get(
    `/wizards/${encodeURIComponent(project)}/${encodeURIComponent(objName)}`)
  return innerModel(res.data?.wizard)
}

export async function saveWizard(
  project: string, objName: string, wizard: WizardModel, layout?: WizardLayout | null,
): Promise<{ wizard: WizardModel | null; check: WizardCheckResult }> {
  const res = await api.put(
    `/wizards/${encodeURIComponent(project)}/${encodeURIComponent(objName)}`,
    layout ? { wizard, layout } : { wizard },
  )
  return { wizard: innerModel(res.data?.wizard), check: asCheck(res.data?.check) }
}

// ── Flow-designer layout ─────────────────────────────────────────────────
//
// Where the flow designer drew each step. Presentation only: the server keeps
// it in the model file BESIDE `wizard`, never inside it, so moving a box does
// not change the model (or its run fingerprint).

export interface WizardLayoutNode { x: number; y: number; w?: number; h?: number }

export interface WizardLayout {
  nodes?: Record<string, WizardLayoutNode>
  viewport?: { x: number; y: number; zoom: number }
}

export async function loadWizardLayout(project: string, objName: string): Promise<WizardLayout> {
  const res = await api.get(
    `/wizards/${encodeURIComponent(project)}/${encodeURIComponent(objName)}/layout`)
  const l = res.data?.layout
  return (l && typeof l === 'object' ? l : {}) as WizardLayout
}

/** Write the layout alone. `false` when the wizard has no file yet — the
 *  layout then rides along with the first model save. */
export async function saveWizardLayout(
  project: string, objName: string, layout: WizardLayout,
): Promise<boolean> {
  const res = await api.put(
    `/wizards/${encodeURIComponent(project)}/${encodeURIComponent(objName)}/layout`,
    { layout },
  )
  return !!res.data?.saved
}

export async function checkWizard(project: string, wizard: WizardModel): Promise<WizardCheckResult> {
  const res = await api.post(`/wizards/${encodeURIComponent(project)}/check`, { wizard })
  return asCheck(res.data)
}

/** Run a scan step's parse rules over a sample payload (server-side — the one
 *  regex dialect and GS1 implementation the runtime uses). */
export async function testWizardScan(
  project: string, wizard: WizardModel, stepId: string, text: string,
  frame?: WizardFrameValues,
): Promise<WizardScanResponse> {
  const res = await api.post(`/wizards/${encodeURIComponent(project)}/test-scan`, {
    wizard, step_id: stepId, text, ...(frame ? { frame } : {}),
  })
  return res.data as WizardScanResponse
}

/** Run a fetch_url step's request (server-side, the same operation the
 *  runtime uses, so the SSRF guard and timeout are the real ones). */
export async function testWizardFetch(
  project: string, wizard: WizardModel, stepId: string, frame?: WizardFrameValues,
): Promise<WizardFetchResponse> {
  const res = await api.post(`/wizards/${encodeURIComponent(project)}/test-fetch`, {
    wizard, step_id: stepId, ...(frame ? { frame } : {}),
  })
  return res.data as WizardFetchResponse
}

/** Render read (RUN_APP): the saved, server-normalised wizard + its base
 *  transaction model. What the runner mounts from. */
export async function loadWizardObject(
  project: string, objName: string,
): Promise<{ wizard: WizardModel; base: TransactionModel | null; user?: { id?: string; name?: string } }> {
  const res = await api.get('/designers/load-wizard-object', {
    params: { project, obj_name: objName },
  })
  return wizardObjectOf(res.data, objName)
}

/** `{wizard, base, user}` out of a wizard render read's answer — the Run
 *  view's `load-wizard-object` and a website's `site-wizard/{obj}` share it. */
export function wizardObjectOf(
  data: unknown, objName: string,
): { wizard: WizardModel; base: TransactionModel | null; user?: { id?: string; name?: string } } {
  const body = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>
  const wizard = innerModel(body.wizard)
  if (!wizard) throw new Error(`Wizard "${objName}" was not found`)
  const rawBase = body.base as Record<string, unknown> | null | undefined
  const base = rawBase && typeof rawBase === 'object'
    ? ((rawBase.transaction && typeof rawBase.transaction === 'object'
      ? rawBase.transaction : rawBase) as unknown as TransactionModel)
    : null
  // Who is running it (the `{user.*}` tokens) — the server's own answer.
  const rawUser = body.user as { id?: unknown; name?: unknown } | null | undefined
  const user = rawUser && rawUser.id
    ? { id: String(rawUser.id), name: rawUser.name == null ? undefined : String(rawUser.name) }
    : undefined
  return { wizard, base, user }
}

// ── Catalog ──────────────────────────────────────────────────────────────

export interface CatalogColumn {
  /** The form key the model targets (`header.<name>` / `line.<name>`). */
  name: string
  db_name?: string
  label?: string
  type?: string
  mandatory?: boolean
  protect?: boolean
  /** item_change mode (`rule` / `business_logic` / `rest_api` …) or null. */
  item_change?: string | null
  /** Declared fill targets of a rule-mode item_change. */
  fills?: string[]
  lookup?: string | null
}

export interface CatalogForm {
  form_no: string
  title?: string
  table?: string
  parent_form?: string | null
  columns: CatalogColumn[]
}

export interface CatalogAction {
  name: string
  label?: string
  handler?: string
  form_no?: string | null
}

export interface CatalogTransaction {
  obj_name: string
  title?: string
  forms: CatalogForm[]
  actions: CatalogAction[]
  /** Line Entry source names per form (for `line_source` / fetch_lines). */
  line_sources?: Record<string, string[]>
}

export interface CatalogNamed {
  obj_name: string
  title?: string
  /** Criteria (V / D) or prompt (R) NAMES; null when the server could not
   *  tell (the pickers then take typed names). */
  params: string[] | null
}

export interface CatalogWizard {
  obj_name: string
  title?: string
  base_object?: string
  mode?: string
  inputs: WizardInput[]
  outputs: string[]
}

export interface CatalogIntent {
  id: string
  title?: string
  parameters: string[]
}

/** A document repository (K) a print step can file into, with its types. */
export interface CatalogRepository {
  obj_name: string
  title?: string
  doctypes: Array<{ key: string; title?: string }>
}

export interface WizardCatalog {
  transactions: CatalogTransaction[]
  repositories: CatalogRepository[]
  visuals: CatalogNamed[]
  dashboards: CatalogNamed[]
  reports: CatalogNamed[]
  wizards: CatalogWizard[]
  intents: CatalogIntent[]
}

type Rec = Record<string, unknown>

/** A list, or a map keyed by name — both arrive; both become a list. */
function listOf(raw: unknown, keyField: string): Rec[] {
  if (Array.isArray(raw)) return raw.filter((x) => x && typeof x === 'object') as Rec[]
  if (raw && typeof raw === 'object') {
    return Object.entries(raw as Rec)
      .filter(([, v]) => v && typeof v === 'object')
      .map(([k, v]) => ({ [keyField]: k, ...(v as Rec) }))
  }
  return []
}

function names(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((x) => (x && typeof x === 'object'
      ? String((x as Rec).name ?? (x as Rec).id ?? '')
      : String(x ?? ''))).filter(Boolean)
  }
  if (raw && typeof raw === 'object') return Object.keys(raw as Rec)
  return []
}

const truthyFlag = (v: unknown) => v === true || v === 1 || v === '1' || v === 'Y' || v === 'y'

function normColumn(raw: Rec): CatalogColumn {
  const ic = raw.item_change
  return {
    name: String(raw.name ?? raw.key ?? raw.db_name ?? ''),
    db_name: raw.db_name != null ? String(raw.db_name) : undefined,
    label: raw.label != null ? String(raw.label) : (raw.heading != null ? String(raw.heading) : undefined),
    type: raw.type != null ? String(raw.type) : undefined,
    mandatory: truthyFlag(raw.mandatory),
    protect: truthyFlag(raw.protect),
    item_change: typeof ic === 'string' ? ic
      : (ic && typeof ic === 'object' ? String((ic as Rec).mode ?? 'rule') : (ic ? 'rule' : null)),
    fills: names(raw.fills ?? raw.fill_targets),
    lookup: raw.lookup != null ? String(raw.lookup) : null,
  }
}

function normTxn(raw: Rec): CatalogTransaction {
  const forms = listOf(raw.forms, 'form_no').map((f) => ({
    form_no: String(f.form_no ?? f.no ?? '1'),
    title: f.title != null ? String(f.title) : undefined,
    table: f.table != null ? String(f.table) : undefined,
    parent_form: f.parent_form != null ? String(f.parent_form) : null,
    columns: listOf(f.columns, 'name').map(normColumn).filter((c) => c.name),
  }))
  const actions = listOf(raw.actions, 'name').map((a) => ({
    name: String(a.name ?? ''),
    label: a.label != null ? String(a.label) : undefined,
    handler: a.handler != null ? String(a.handler) : undefined,
    form_no: a.form_no != null ? String(a.form_no) : null,
  })).filter((a) => a.name)
  const ls = raw.line_sources
  const lineSources: Record<string, string[]> = {}
  if (ls && typeof ls === 'object' && !Array.isArray(ls)) {
    for (const [k, v] of Object.entries(ls as Rec)) lineSources[k] = names(v)
  }
  return {
    obj_name: String(raw.obj_name ?? raw.name ?? ''),
    title: raw.title != null ? String(raw.title) : undefined,
    forms, actions, line_sources: lineSources,
  }
}

function normNamed(raw: Rec): CatalogNamed {
  const p = raw.params ?? raw.criteria ?? raw.prompts ?? raw.parameters
  return {
    obj_name: String(raw.obj_name ?? raw.name ?? ''),
    title: raw.title != null ? String(raw.title) : undefined,
    params: p === null || p === undefined ? null : names(p),
  }
}

export async function wizardCatalog(project: string): Promise<WizardCatalog> {
  const res = await api.get(`/wizards/${encodeURIComponent(project)}/catalog`)
  const d = (res.data || {}) as Rec
  return {
    transactions: listOf(d.transactions, 'obj_name').map(normTxn).filter((t) => t.obj_name),
    repositories: listOf(d.repositories, 'obj_name').map((r) => ({
      obj_name: String(r.obj_name ?? ''),
      title: r.title != null ? String(r.title) : undefined,
      doctypes: (Array.isArray(r.doctypes) ? r.doctypes as Rec[] : [])
        .map((t) => ({ key: String(t.key ?? ''), title: t.title != null ? String(t.title) : undefined }))
        .filter((t) => t.key),
    })).filter((r) => r.obj_name),
    visuals: listOf(d.visuals, 'obj_name').map(normNamed).filter((o) => o.obj_name),
    dashboards: listOf(d.dashboards, 'obj_name').map(normNamed).filter((o) => o.obj_name),
    reports: listOf(d.reports, 'obj_name').map(normNamed).filter((o) => o.obj_name),
    wizards: listOf(d.wizards, 'obj_name').map((w) => ({
      obj_name: String(w.obj_name ?? w.name ?? ''),
      title: w.title != null ? String(w.title) : undefined,
      base_object: w.base_object != null ? String(w.base_object) : undefined,
      mode: w.mode != null ? String(w.mode) : undefined,
      inputs: (Array.isArray(w.inputs) ? w.inputs : []) as WizardInput[],
      outputs: names(w.outputs),
    })).filter((w) => w.obj_name),
    intents: listOf(d.intents, 'id').map((i) => ({
      id: String(i.id ?? i.intent_id ?? i.name ?? ''),
      title: i.title != null ? String(i.title) : undefined,
      parameters: names(i.parameters ?? i.params),
    })).filter((i) => i.id),
  }
}
